// Types for the Daily Intake / Retry Console / Patient Registry pages.
// Shapes mirror the backend endpoints in `backend/main.py`:
//   GET  /api/dashboard/intake/days
//   GET  /api/dashboard/intake/day
//   GET  /api/dashboard/patients
//   GET  /api/dashboard/patients/{patient_key}
//   POST /api/dashboard/retry/preflight
//   POST /api/dashboard/retry/execute

export type FailClass =
  | "live_outcome"
  | "not_submitted"
  | "in_progress"
  | "pending_verification"
  | "pending"
  | "telephony_error"
  | "no_dialable_phone"
  | "wrong_number_retryable"
  | "retryable_other"
  | "exhausted_live_agent"
  | "exhausted_other"
  | "other";

/** One row per local-tz date returned by `/api/dashboard/intake/days`. */
export interface IntakeDay {
  date: string;
  total: number;
  completed: number;
  failed: number;
  in_flight: number;
  retryable: number;
  exhausted: number;
  duplicates_seen_before: number;
  duplicates_within_day: number;
  fail_classes: Partial<Record<FailClass, number>>;
}

export interface IntakeDaysResponse {
  days: IntakeDay[];
  tz: string;
  lookback: number;
  case_count: number;
}

/** Slim case shape used across intake / retry / patients pages. */
export interface IntakeCase {
  case_id: string;
  document_id: string;
  created_at: string | null;
  local_date: string | null;
  updated_at: string | null;
  status: string;
  success: string;
  fail_class: FailClass;
  fail_reason: string;
  pa_status: string;
  fail_details: string;
  patient_key: string | null;
  is_duplicate_patient: boolean;
  member_first_name: string;
  member_last_name: string;
  member_dob: string;
  member_id: string;
  member_bin: string;
  member_pcn: string;
  payer_name: string;
  structured_output_payer_name?: string | null;
  payer_phone_number: string;
  product_name: string;
  attempts: number;
  total_call_attempts: number;
  vapi_call_count: number;
  tried_phone_numbers: string[];
  available_phone_numbers: string[];
  all_numbers_exhausted: boolean;
  outbound_deferred_queued: boolean;
  outbound_scheduled_for: string | null;
  current_batch_id: string | null;
  alternate_contact_number: string | null;
  /**
   * Set when the webhook auto-retry loop paused this case after two consecutive
   * identical not_submitted_* outcomes. Operators can still manually retry from
   * the Retry Console; the value is informational (e.g. ``repeated_not_submitted_live``).
   */
  auto_retry_paused_reason: string | null;
  /** Global per-number success rates keyed by the case's dial string. */
  phone_global_success_rates?: Record<string, number>;
  /**
   * P(at least one success) if the Retry Console re-runs all listed numbers:
   * ``1 - prod(1 - rate_i)`` using ``phone_global_success_rates``.
   */
  retry_success_likelihood?: number | null;
}

/** Lightweight KPI shape derived client-side from `IntakeDay`. */
export interface IntakeDayKpis {
  total: number;
  completed: number;
  not_submitted: number;
  in_flight: number;
  retryable: number;
  exhausted: number;
  no_dialable_phone: number;
  duplicate_patients: number;
}

/** Paginated /api/dashboard/intake/day response. */
export interface IntakeDayResponse {
  date: string;
  tz: string;
  cases: IntakeCase[];
  has_more: boolean;
  next_cursor: string | null;
  duplicate_lookback_used: number;
  duplicate_detection_applied: boolean;
}

export interface PatientGroup {
  patient_key: string;
  first_name: string;
  last_name: string;
  dob: string;
  case_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  first_seen_date: string | null;
  last_seen_date: string | null;
  member_ids: string[];
  payers: string[];
  products: string[];
  fail_classes: FailClass[];
  case_ids: string[];
}

/** Case-level intake buckets for the patient registry lookback window (Daily Intake semantics). */
export interface IntakeWindowCaseKpis {
  total: number;
  completed: number;
  not_submitted: number;
  in_flight: number;
  retryable: number;
  exhausted: number;
  no_dialable_phone: number;
}

/** Patient registry: one bucket per patient from their most recent case in the window. */
export type IntakeWindowPatientKpis = IntakeWindowCaseKpis;

export interface PatientsResponse {
  patients: PatientGroup[];
  patient_count: number;
  returned_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  case_count: number;
  unkeyed_case_count: number;
  tz: string;
  lookback: number;
  window_case_kpis?: IntakeWindowCaseKpis;
  window_patient_kpis?: IntakeWindowPatientKpis;
}

export interface PatientDetailResponse {
  patient_key: string;
  first_name: string;
  last_name: string;
  dob: string;
  case_count: number;
  cases: IntakeCase[];
  tz: string;
  lookback: number;
}

export interface RetryOverride {
  payer_phone_number?: string;
  available_phone_numbers?: string[];
}

export interface RetryRequestBody {
  case_ids: string[];
  overrides?: Record<string, RetryOverride>;
  retry_product_name?: string;
  batch_size?: number;
  batch_offset_minutes?: number;
}

export interface RetryPhoneFields {
  payer_phone_number: string;
  available_phone_numbers: string[];
  tried_phone_numbers: string[];
  alternate_contact_number: string | null;
  all_numbers_exhausted: boolean;
}

export interface RetryReadyRow extends RetryPhoneFields {
  case_id: string;
  name: string;
  product_name: string;
  payer_name: string;
  fail_class: FailClass;
  fail_reason: string;
  attempts: number;
}

export interface RetryNeedsInputRow extends RetryReadyRow {
  reason: "needs_phone_number";
  missing_fields: string[];
  invalid_phone?: string | null;
  suggested_phone?: string | null;
}

export interface RetryCaseOverrides {
  payer_phone_number?: string;
  available_phone_numbers?: string[];
}

export interface RetryBlockedRow {
  case_id: string;
  reason: string;
  detail?: string;
  batch_id?: string;
  outbound_scheduled_for?: string | null;
  missing_fields?: string[];
  name?: string;
  payer_name?: string;
  payer_phone_number?: string;
  fail_class?: FailClass;
  fail_reason?: string;
  attempts?: number;
}

export interface RetryPreflightResponse {
  ready: RetryReadyRow[];
  needs_input: RetryNeedsInputRow[];
  blocked: RetryBlockedRow[];
  summary: {
    total: number;
    ready: number;
    needs_input: number;
    blocked: number;
  };
}

export interface RetryReleaseBatchHoldResult {
  case_id: string;
  ok: boolean;
  released?: boolean;
  batch_id?: string | null;
  case_removed?: boolean | null;
  still_in_active_batch?: boolean;
  detail?: string;
  error?: string;
}

export interface RetryReleaseBatchHoldResponse {
  results: RetryReleaseBatchHoldResult[];
}

export interface RetryExecuteResponse {
  success: boolean;
  message?: string;
  batch_id?: string;
  assistant_id?: string;
  cases_total?: number;
  cases_processed?: number;
  cases_created?: number;
  cases_skipped?: number;
  cases_invalid?: number;
  cases_errors?: number;
  errors?: { case_id?: string; error: string }[];
  invalid_cases?: { case_id?: string | null; missing_fields: string[] }[];
  retry_input_count?: number;
  retry_fetch_errors?: number;
  deferred_telephony?: boolean;
}

/** Display label for a fail_class. */
export const FAIL_CLASS_LABEL: Record<FailClass, string> = {
  live_outcome: "PA outcome",
  not_submitted: "Not submitted",
  in_progress: "In progress",
  pending_verification: "Pending verification",
  pending: "Pending",
  telephony_error: "Telephony error",
  no_dialable_phone: "No dialable phone",
  wrong_number_retryable: "Wrong number (retryable)",
  retryable_other: "Failed (retryable)",
  exhausted_live_agent: "Exhausted: live agent",
  exhausted_other: "Exhausted",
  other: "Other",
};

/** Tone for a fail_class when shown as a badge. */
export const FAIL_CLASS_TONE: Record<
  FailClass,
  "success" | "failure" | "warning" | "info" | "neutral" | "muted"
> = {
  live_outcome: "success",
  not_submitted: "warning",
  in_progress: "info",
  pending_verification: "warning",
  pending: "muted",
  telephony_error: "failure",
  no_dialable_phone: "failure",
  wrong_number_retryable: "warning",
  retryable_other: "failure",
  exhausted_live_agent: "failure",
  exhausted_other: "failure",
  other: "neutral",
};

/** Buckets that the user can retry with one click. */
export const RETRYABLE_FAIL_CLASSES: FailClass[] = [
  "telephony_error",
  "wrong_number_retryable",
  "retryable_other",
];

/** Matches intake KPI "In flight" (pending / active call / BIN–PCN verification). */
export const IN_FLIGHT_FAIL_CLASSES: FailClass[] = [
  "pending",
  "in_progress",
  "pending_verification",
];
